import { FC, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { ErrorMessage, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError, parseApiErrors } from '../../../utils/handleApiError'
import { KTSVG } from '../../../../_metronic/helpers'
import { UserModel } from '../../../modules/auth/models/UserModel'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../../setup'
import { DatePicker } from 'antd'
import dayjs from "dayjs";
import { SelectExperienceField } from './fields/SelectExperienceField'
import { MembersAutocompleteField } from './fields/MembersAutocompleteField'
import { PartnersAutocompleteField } from './fields/PartnersAutocompleteField'
import { createTask, getTasksById, updateTask } from '../../../services/adminTicketsService'

// The detail endpoint hydrates each relation as an object (so chips can render
// without an extra roundtrip); Formik only cares about the id arrays, so we
// split: ids go into the form values, full objects go to the fields as
// `preloaded` to seed their chip caches.
const toId = (v: any): number | null => {
    if (v == null) return null
    if (typeof v === 'number') return v
    if (typeof v === 'object' && typeof v.id === 'number') return v.id
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}
const toIds = (arr: any[]): number[] =>
    (arr ?? []).map(toId).filter((v): v is number => v !== null)
const toObjects = (arr: any[]): any[] =>
    (arr ?? []).filter((v) => v && typeof v === 'object')

type Props = {
    show: boolean;
    onHide: () => void;
    ticketId?:number;
}

export const TaskModal: FC<Props> = ({ show, onHide, ticketId = 0 }) => {
    const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const formikRef = useRef<FormikProps<any>>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    // Kept alongside Formik so the autocomplete fields can render the already-
    // selected chips on edit before any search fires.
    const [preloadedMembers, setPreloadedMembers] = useState<any[]>([]);
    const [preloadedPartners, setPreloadedPartners] = useState<any[]>([]);
    const [preloadedExperiences, setPreloadedExperiences] = useState<any[]>([]);

    const setFormikValues = async (ticket:any) => {
        await formikRef.current.setValues({
            id: ticketId ?? 0,
            user_id: ticket?.userId ?? ticketId ?? 0,
            title: ticket?.title ?? "",
            notes: ticket?.notes ?? "",
            status: ticket?.status ?? "pending",
            due_date: ticket?.dueDate ?? dayjs(),
            related_members: toIds(ticket?.relatedMembers),
            related_experiences: toIds(ticket?.relatedExperiences),
            related_partners: toIds(ticket?.relatedPartners),
        });
        setPreloadedMembers(toObjects(ticket?.relatedMembers));
        setPreloadedPartners(toObjects(ticket?.relatedPartners));
        setPreloadedExperiences(toObjects(ticket?.relatedExperiences));
    };

    useEffect(()=>{
        if (ticketId > 0){
            getTicketById();
        }
    },[ticketId])

    const getTicketById = async () => {
        let ticket:any = await getTasksById(ticketId);
        setFormikValues(ticket);
    }

    const initialValues = {
        id: ticketId ?? 0,
        user_id: 0,
        title: "",
        notes: "",
        due_date: dayjs(),
        status: "pending",
        related_members: [] as number[],
        related_experiences: [] as number[],
        related_partners: [] as number[],
    };

    const validationSchema = Yup.object({
        id: Yup.number(),
        waiting_list: Yup.number(),
        user_id: Yup.number(),
        title: Yup.string().required("title is required").max(100, "Max 500 characters"),
        notes: Yup.string().required("Note is required").max(500, "Max 500 characters"),
        due_date: Yup.string().required("Date is required"),
        related_members: Yup.array().of(Yup.number()),
        related_experiences: Yup.array().of(Yup.number()),
        related_partners: Yup.array().of(Yup.number()),
    });

    const resetForm = () => {
        formikRef.current.resetForm({
            values: {
                user_id: 0,
                title: "",
                notes: "",
                due_date: dayjs(),
                status: "pending",
                related_members: [],
                related_experiences: [],
                related_partners: []
            }
        });
        setPreloadedMembers([]);
        setPreloadedPartners([]);
        setPreloadedExperiences([]);
    }

    const onCancelHandle = () => {
        resetForm();
        onHide();
    }

    const handleSubmit = async (values) => {
        try {
            if(ticketId == 0){
                let isCreated = await createTask({...values});
                if(isCreated?.status == 201){
                    showErrorAlert({ title: '', message: 'Your task has been created successfully.' });
                }
            }else{
                let isUpdate = await updateTask(ticketId,{...values});
                if(isUpdate?.status == 200){
                    showErrorAlert({ title: '', message: 'Your task has been updated successfully.' });
                }
            }
            onHide();
        } catch (error) {
            handleApiError(error, {
                onServerError: ()=>showErrorAlert({ title: 'Error', message: 'The task could not be created. Please try again.' }),
                onBadRequest: (data)=>{
                    let msg = parseApiErrors(data);
                    showErrorAlert({ title: 'Error', message: msg })
                }
            });
        }
    }

    return (
        <Modal show={show} centered size="lg" backdrop="static" dialogClassName="team-admin-add-member-modal">
            <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {(formik) => {

                    const handleMembersChange = (value:string) => {
                        formik.setFieldValue("related_members", value);
                        formik.validateForm();
                    };
                    
                    const handlePartnersChange = (value:string) => {
                        formik.setFieldValue("related_partners", value);
                        formik.validateForm();
                    };
                    
                    const handleExperiencesChange = (value:string) => {
                        formik.setFieldValue("related_experiences", value);
                        formik.validateForm();
                    };

                    const handleTextAreaInput = (e) => {
                        const el = textareaRef.current;
                        if (!el) return;
                        el.style.height = "auto"; // resetea para medir bien
                        const lineHeight = 20;    // ajusta según tu font-size
                        const maxHeight = 5 * lineHeight;

                        el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
                    };

                    return (<Form>
                        <div className="team-admin-add-member">
                            {/* HEADER */}
                            <div className="admin-experience-details-header">
                                <h2 className="nb-invite-modal__title">
                                    {formik.values.user_id == 0 ? 'Add' : 'Edit'} Task
                                </h2>
                                <div className="rm-style-3 cursor-pointer" onClick={onCancelHandle}>
                                    <KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
                                </div>
                            </div>
                            {/* CONTENT */}
                            <div className="team-admin-add-member__card">
                                <div className="team-admin-add-member__section">
                                    <div className="team-admin-add-member__section-title">
                                        Task Information
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Name</label>
                                        <div className="tap-add-experience-2-input-line">
                                            <input className="tap-add-experience-2-input" name="title" value={formik.values.title} onChange={formik.handleChange}  maxLength={100} />
                                        </div>
                                        <ErrorMessage name="title" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Note</label>
                                        <div className="tap-add-experience-2-input-line h-auto">
                                            <textarea name="notes" value={formik.values.notes} onChange={formik.handleChange} className="tap-add-experience-2-input textarea-autosize" onInput={handleTextAreaInput} ref={textareaRef} maxLength={500} />
                                        </div>
                                        <ErrorMessage name="notes" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Date &amp; time</label>
                                        <DatePicker
                                            className="underline-picker"
                                            showTime={{ format: 'HH:mm' }}
                                            format="YYYY-MM-DD HH:mm"
                                            placeholder="YYYY-MM-DD HH:mm"
                                            value={
                                                formik.values.due_date
                                                ? dayjs(formik.values.due_date)
                                                : null
                                            }
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                'due_date',
                                                value ? value.toISOString() : null
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="team-admin-add-member__field">
                                    <label>Status</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="status" value={formik.values.status} onChange={formik.handleChange} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={'pending'}>Pending</option>
                                            <option value={'in_progress'}>In Progress</option>
                                            <option value={'resolved'}>Resolved</option>
                                            <option value={'cancelled'}>Cancelled</option>
                                        </select>
                                    </div>
                                </div>

                                {/* ASSIGN MEMBERS */}
                                <div className="team-admin-add-member__field">
                                    <label>Related Member</label>
                                    <MembersAutocompleteField values={formik.values.related_members} onChange={handleMembersChange} name='related_members' preloaded={preloadedMembers}/>
                                    <ErrorMessage name="related_members" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                {/* ASSIGN EXPERIENCE */}
                                <div className="team-admin-add-member__field">
                                    <label>Related experience</label>
                                    <SelectExperienceField values={formik.values.related_experiences} onChange={handleExperiencesChange} name='related_experiences' preloaded={preloadedExperiences}/>
                                    <ErrorMessage name="related_experiences" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                {/* ASSIGN PARTNERS */}
                                <div className="team-admin-add-member__field">
                                    <label>Related Partners</label>
                                    <PartnersAutocompleteField values={formik.values.related_partners} onChange={handlePartnersChange} name='related_partners' preloaded={preloadedPartners}/>
                                    <ErrorMessage name="related_partners" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                            </div>

                            {/* FOOTER */}
                            <div className="team-admin-add-member__footer">
                                <button type='button' className="decline-experience__btn decline-experience__btn--secondary" onClick={onCancelHandle}>
                                    Cancel
                                </button>

                                <button type='submit' className="decline-experience__btn decline-experience__btn--primary">
                                    {formik.values.user_id == 0 ? 'Add' : 'Edit'} Task
                                    &nbsp;<KTSVG path='/media/svg/nobilis/vector02.svg' />
                                </button>
                            </div>
                        </div>
                    </Form>);
                }}
            </Formik>
        </Modal>
    )
}
